import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Minus, Trash2, ShoppingBag, Clock, Phone, MessageCircle, Star } from "lucide-react";

const WHATSAPP_NUMBER = "447424864732";

const OrderPage = () => {
  const [cart, setCart] = useState([]);
  
  // Meal Deal state
  const [mealDealBacon, setMealDealBacon] = useState("streaky");
  const [mealDealBread, setMealDealBread] = useState("farmhouse");
  const [mealDealSauce, setMealDealSauce] = useState("none");
  const [mealDealThickCut, setMealDealThickCut] = useState(false);
  const [mealDealDoublePig, setMealDealDoublePig] = useState(false);
  const [mealDealDrink, setMealDealDrink] = useState("tea-white");

  // Sandwich state
  const [baconType, setBaconType] = useState("streaky");
  const [breadType, setBreadType] = useState("farmhouse");
  const [sauce, setSauce] = useState("none");
  const [thickCut, setThickCut] = useState(false);
  const [doublePig, setDoublePig] = useState(false);

  const calculateSandwichPrice = (thick, double) => {
    let price = 3.50;
    if (thick) price += 1.00;
    if (double) price += 2.00;
    return price;
  };

  const calculateMealDealPrice = () => {
    let price = 4.95;
    if (mealDealThickCut) price += 1.00;
    if (mealDealDoublePig) price += 2.00;
    return price;
  };

  const addMealDealToCart = () => {
    const upgrades = [];
    if (mealDealThickCut) upgrades.push("Thick Cut");
    if (mealDealDoublePig) upgrades.push("Double the Pig");
    
    const drinkName = mealDealDrink.includes("tea") ? "Tea" : "Coffee";
    const drinkStyle = mealDealDrink.includes("white") ? "White" : "Black";
    
    const mealDeal = {
      id: "mealdeal-" + Date.now(),
      name: "MEAL DEAL",
      details: mealDealBacon.charAt(0).toUpperCase() + mealDealBacon.slice(1) + " bacon on " + mealDealBread + (mealDealSauce !== "none" ? " with " + mealDealSauce : "") + " + " + drinkName + " (" + drinkStyle + ") + Hashbrown",
      upgrades: upgrades.length > 0 ? upgrades.join(", ") : null,
      price: calculateMealDealPrice(),
      quantity: 1,
      type: "mealdeal"
    };
    setCart(prev => [...prev, mealDeal]);
    toast.success("Meal Deal added!");
    
    // Reset upgrades
    setMealDealThickCut(false);
    setMealDealDoublePig(false);
  };

  const addSandwichToCart = () => {
    const upgrades = [];
    if (thickCut) upgrades.push("Thick Cut");
    if (doublePig) upgrades.push("Double the Pig");
    
    const sandwich = {
      id: "sandwich-" + Date.now(),
      name: "Bacon Sandwich",
      details: baconType.charAt(0).toUpperCase() + baconType.slice(1) + " bacon on " + breadType + (sauce !== "none" ? " with " + sauce : ""),
      upgrades: upgrades.length > 0 ? upgrades.join(", ") : null,
      price: calculateSandwichPrice(thickCut, doublePig),
      quantity: 1,
      type: "sandwich"
    };
    setCart(prev => [...prev, sandwich]);
    toast.success("Bacon Sandwich added!");
    
    // Reset upgrades
    setThickCut(false);
    setDoublePig(false);
  };

  const addItemToCart = (item) => {
    // For simple items (hashbrown, drinks), we can stack quantities
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    toast.success(item.name + " added!");
  };

  const updateQty = (cartIndex, delta) => {
    setCart(prev => prev.map((item, idx) => {
      if (idx === cartIndex) {
        const newQty = item.quantity + delta;
        return newQty <= 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const removeItem = (cartIndex) => {
    setCart(prev => prev.filter((_, idx) => idx !== cartIndex));
    toast.info("Item removed");
  };

  const getTotal = () => cart.reduce((total, item) => total + (item.price * item.quantity), 0);

  const handleWhatsApp = () => {
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    let msg = "Hi! I'd like to place an order:\n\n";
    cart.forEach(item => {
      msg += "• " + item.quantity + "x " + item.name;
      if (item.details) msg += " (" + item.details + ")";
      if (item.upgrades) msg += " [" + item.upgrades + "]";
      msg += " - £" + (item.price * item.quantity).toFixed(2) + "\n";
    });
    msg += "\nTotal: £" + getTotal().toFixed(2) + "\n\nThanks!";
    window.open("https://wa.me/" + WHATSAPP_NUMBER + "?text=" + encodeURIComponent(msg), "_blank");
  };

  const drinks = [
    { id: "tea-white", name: "Cuppa Tea (White)", price: 1.50 },
    { id: "tea-black", name: "Cuppa Tea (Black)", price: 1.50 },
    { id: "coffee-white", name: "Cuppa Coffee (White)", price: 1.95 },
    { id: "coffee-black", name: "Cuppa Coffee (Black)", price: 1.95 },
  ];

  const sides = [
    { id: "hashbrown", name: "Hashbrown", price: 1.50 },
  ];

  const OptionButton = ({ selected, onClick, children }) => (
    <Button
      variant={selected ? "brand" : "outline"}
      size="sm"
      onClick={onClick}
      className="flex-1"
    >
      {children}
    </Button>
  );

  return (
    <div className="page-transition">
      <section className="bg-gradient-hero py-12 lg:py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-primary-foreground mb-4">
            Order via WhatsApp
          </h1>
          <p className="text-primary-foreground/80 text-lg max-w-xl mx-auto">
            Build your order below, then send it to us on WhatsApp.
          </p>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              
              {/* MEAL DEAL BUILDER */}
              <Card className="bg-card border-2 border-wheat/50 shadow-elevated">
                <CardHeader className="bg-wheat/10">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <Star className="h-6 w-6 text-wheat fill-wheat" />
                      <CardTitle className="font-display text-2xl text-foreground">Meal Deal</CardTitle>
                      <Badge className="bg-wheat text-wheat-foreground">Best Value</Badge>
                    </div>
                    <span className="font-display text-2xl font-bold text-accent">£{calculateMealDealPrice().toFixed(2)}</span>
                  </div>
                  <p className="text-muted-foreground">Sandwich + Tea or Coffee + Hashbrown</p>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  {/* Choose Bacon */}
                  <div>
                    <p className="text-sm font-medium text-foreground mb-3">Choose Bacon</p>
                    <div className="flex gap-3">
                      <OptionButton selected={mealDealBacon === "streaky"} onClick={() => setMealDealBacon("streaky")}>Streaky</OptionButton>
                      <OptionButton selected={mealDealBacon === "back"} onClick={() => setMealDealBacon("back")}>Back</OptionButton>
                    </div>
                  </div>

                  {/* Upgrades */}
                  <div className="border-t border-border pt-4">
                    <p className="text-sm font-medium text-foreground mb-3">Upgrades</p>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                        <div>
                          <span className="font-medium text-foreground">Thick Cut Bacon</span>
                          <span className="text-accent ml-2">+£1.00</span>
                        </div>
                        <Button variant={mealDealThickCut ? "brand" : "outline"} size="sm" onClick={() => setMealDealThickCut(!mealDealThickCut)}>
                          {mealDealThickCut ? "Added" : "Add"}
                        </Button>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                        <div>
                          <span className="font-medium text-foreground">Double the Pig</span>
                          <span className="text-accent ml-2">+£2.00</span>
                        </div>
                        <Button variant={mealDealDoublePig ? "brand" : "outline"} size="sm" onClick={() => setMealDealDoublePig(!mealDealDoublePig)}>
                          {mealDealDoublePig ? "Added" : "Add"}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Choose Bread */}
                  <div className="border-t border-border pt-4">
                    <p className="text-sm font-medium text-foreground mb-3">Choose Bread</p>
                    <div className="flex gap-3 flex-wrap">
                      <OptionButton selected={mealDealBread === "farmhouse"} onClick={() => setMealDealBread("farmhouse")}>Farmhouse</OptionButton>
                      <OptionButton selected={mealDealBread === "seeded"} onClick={() => setMealDealBread("seeded")}>Seeded</OptionButton>
                      <OptionButton selected={mealDealBread === "sourdough"} onClick={() => setMealDealBread("sourdough")}>Sourdough</OptionButton>
                    </div>
                  </div>

                  {/* Add Sauce */}
                  <div className="border-t border-border pt-4">
                    <p className="text-sm font-medium text-foreground mb-3">Add Sauce</p>
                    <div className="flex gap-3 flex-wrap">
                      <OptionButton selected={mealDealSauce === "ketchup"} onClick={() => setMealDealSauce("ketchup")}>Ketchup</OptionButton>
                      <OptionButton selected={mealDealSauce === "hp"} onClick={() => setMealDealSauce("hp")}>HP</OptionButton>
                      <OptionButton selected={mealDealSauce === "none"} onClick={() => setMealDealSauce("none")}>None</OptionButton>
                    </div>
                  </div>

                  {/* Choose Drink */}
                  <div className="border-t border-border pt-4">
                    <p className="text-sm font-medium text-foreground mb-3">Choose Your Drink</p>
                    <div className="grid grid-cols-2 gap-3">
                      <OptionButton selected={mealDealDrink === "tea-white"} onClick={() => setMealDealDrink("tea-white")}>Tea (White)</OptionButton>
                      <OptionButton selected={mealDealDrink === "tea-black"} onClick={() => setMealDealDrink("tea-black")}>Tea (Black)</OptionButton>
                      <OptionButton selected={mealDealDrink === "coffee-white"} onClick={() => setMealDealDrink("coffee-white")}>Coffee (White)</OptionButton>
                      <OptionButton selected={mealDealDrink === "coffee-black"} onClick={() => setMealDealDrink("coffee-black")}>Coffee (Black)</OptionButton>
                    </div>
                  </div>

                  {/* Hashbrown included */}
                  <div className="border-t border-border pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span className="text-green-600">✓</span>
                      <span>Hashbrown included</span>
                    </div>
                  </div>

                  <Button variant="hero" size="lg" className="w-full bg-wheat hover:bg-wheat/90 text-wheat-foreground" onClick={addMealDealToCart}>
                    <Plus className="h-5 w-5 mr-2" />
                    Add Meal Deal to Order
                  </Button>
                </CardContent>
              </Card>

              {/* INDIVIDUAL ITEMS SECTION */}
              <div className="text-center py-4">
                <p className="text-muted-foreground">— or order items individually —</p>
              </div>
              
              {/* Bacon Sandwich Builder */}
              <Card className="bg-card border-border shadow-card">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="font-display text-2xl text-foreground">Bacon Sandwich</CardTitle>
                    <span className="font-display text-2xl font-bold text-accent">£{calculateSandwichPrice(thickCut, doublePig).toFixed(2)}</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Choose Bacon */}
                  <div>
                    <p className="text-sm font-medium text-foreground mb-3">Choose Bacon</p>
                    <div className="flex gap-3">
                      <OptionButton selected={baconType === "streaky"} onClick={() => setBaconType("streaky")}>Streaky</OptionButton>
                      <OptionButton selected={baconType === "back"} onClick={() => setBaconType("back")}>Back</OptionButton>
                    </div>
                  </div>

                  {/* Upgrades */}
                  <div className="border-t border-border pt-4">
                    <p className="text-sm font-medium text-foreground mb-3">Upgrades</p>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                        <div>
                          <span className="font-medium text-foreground">Thick Cut Bacon</span>
                          <span className="text-accent ml-2">+£1.00</span>
                        </div>
                        <Button variant={thickCut ? "brand" : "outline"} size="sm" onClick={() => setThickCut(!thickCut)}>
                          {thickCut ? "Added" : "Add"}
                        </Button>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                        <div>
                          <span className="font-medium text-foreground">Double the Pig</span>
                          <span className="text-accent ml-2">+£2.00</span>
                        </div>
                        <Button variant={doublePig ? "brand" : "outline"} size="sm" onClick={() => setDoublePig(!doublePig)}>
                          {doublePig ? "Added" : "Add"}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Choose Bread */}
                  <div className="border-t border-border pt-4">
                    <p className="text-sm font-medium text-foreground mb-3">Choose Bread</p>
                    <div className="flex gap-3 flex-wrap">
                      <OptionButton selected={breadType === "farmhouse"} onClick={() => setBreadType("farmhouse")}>Farmhouse</OptionButton>
                      <OptionButton selected={breadType === "seeded"} onClick={() => setBreadType("seeded")}>Seeded</OptionButton>
                      <OptionButton selected={breadType === "sourdough"} onClick={() => setBreadType("sourdough")}>Sourdough</OptionButton>
                    </div>
                  </div>

                  {/* Add Sauce */}
                  <div className="border-t border-border pt-4">
                    <p className="text-sm font-medium text-foreground mb-3">Add Sauce</p>
                    <div className="flex gap-3 flex-wrap">
                      <OptionButton selected={sauce === "ketchup"} onClick={() => setSauce("ketchup")}>Ketchup</OptionButton>
                      <OptionButton selected={sauce === "hp"} onClick={() => setSauce("hp")}>HP</OptionButton>
                      <OptionButton selected={sauce === "none"} onClick={() => setSauce("none")}>None</OptionButton>
                    </div>
                  </div>

                  <Button variant="hero" size="lg" className="w-full" onClick={addSandwichToCart}>
                    <Plus className="h-5 w-5 mr-2" />
                    Add Bacon Sandwich to Order
                  </Button>
                </CardContent>
              </Card>

              {/* Sides */}
              <Card className="bg-card border-border shadow-card">
                <CardHeader>
                  <CardTitle className="font-display text-2xl text-foreground">Sides</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {sides.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors">
                      <span className="font-medium text-foreground">{item.name}</span>
                      <div className="flex items-center gap-4">
                        <span className="font-display font-bold text-accent">£{item.price.toFixed(2)}</span>
                        <Button variant="brand" size="sm" onClick={() => addItemToCart(item)}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Hot Drinks */}
              <Card className="bg-card border-border shadow-card">
                <CardHeader>
                  <CardTitle className="font-display text-2xl text-foreground">Hot Drinks</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {drinks.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors">
                      <span className="font-medium text-foreground">{item.name}</span>
                      <div className="flex items-center gap-4">
                        <span className="font-display font-bold text-accent">£{item.price.toFixed(2)}</span>
                        <Button variant="tea" size="sm" onClick={() => addItemToCart(item)}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="bg-card border-border shadow-elevated sticky top-24">
                <CardHeader>
                  <CardTitle className="font-display text-2xl text-foreground flex items-center gap-2">
                    <ShoppingBag className="h-6 w-6 text-accent" />
                    Your Order
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {cart.length === 0 ? (
                    <div className="text-center py-8">
                      <ShoppingBag className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-muted-foreground">Your order is empty</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {cart.map((item, idx) => (
                        <div key={idx} className={`p-3 rounded-lg ${item.type === "mealdeal" ? "bg-wheat/10 border border-wheat/30" : "bg-secondary"}`}>
                          <div className="flex items-start justify-between mb-1">
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm ${item.type === "mealdeal" ? "text-accent" : "text-foreground"}`}>{item.name}</p>
                              {item.details && <p className="text-xs text-muted-foreground">{item.details}</p>}
                              {item.upgrades && <p className="text-xs text-accent">{item.upgrades}</p>}
                            </div>
                            <span className="text-sm font-bold text-accent ml-2">£{(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                          <div className="flex items-center gap-1 mt-2">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => updateQty(idx, -1)}>
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => updateQty(idx, 1)}>
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive ml-auto" onClick={() => removeItem(idx)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {cart.length > 0 && (
                    <div className="pt-4 border-t border-border">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-medium text-foreground">Total</span>
                        <span className="font-display text-2xl font-bold text-accent">£{getTotal().toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </CardContent>

                {cart.length > 0 && (
                  <CardFooter className="flex-col gap-3">
                    <Button onClick={handleWhatsApp} size="lg" className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white">
                      <MessageCircle className="h-5 w-5 mr-2" />
                      Order via WhatsApp
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">Opens WhatsApp with your order</p>
                  </CardFooter>
                )}
              </Card>

              <Card className="bg-secondary border-border mt-6">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-wheat shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-foreground">Pickup Only</p>
                      <p className="text-muted-foreground">59 Torquay Road, Paignton</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-foreground">Call us</p>
                      <a href="tel:+447424864732" className="text-accent hover:underline">07424 864732</a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-secondary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display text-2xl font-bold text-foreground mb-4">Prefer to Call?</h2>
          <p className="text-muted-foreground mb-6">Give us a ring and we'll sort your order over the phone.</p>
          <Button asChild variant="brand" size="lg">
            <a href="tel:+447424864732">
              <Phone className="h-5 w-5 mr-2" />
              Call 07424 864732
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default OrderPage;
